/**
 * Meta (Facebook / Instagram) Pixel events.
 *
 * The Pixel itself is already loaded in public/index.html, where it is
 * initialised and fires PageView. Until now that was the only event on the
 * whole site, which is enough to build audiences but not enough for catalogue
 * ads: dynamic retargeting needs to know *which product* was viewed, added or
 * bought.
 *
 * That link is `content_ids`. The values here are listing UUIDs, which is
 * exactly the `id` column of the catalogue feed (see
 * server/api-util/merchantFeed/metaCatalog.js). If the two ever drift apart,
 * Meta silently stops matching events to products, so they must stay the same.
 *
 * Every function is safe to call anywhere: on the server during rendering
 * there is no `window`, and if the Pixel script is blocked or still loading
 * there is no `fbq`. In both cases the call does nothing rather than throwing.
 */

// Meta's value for a single purchasable product.
const CONTENT_TYPE_PRODUCT = 'product';

/** The Pixel function, or null when it is not available (server, blocked). */
const getFbq = () =>
  typeof window !== 'undefined' && typeof window.fbq === 'function' ? window.fbq : null;

/**
 * Send one standard Pixel event.
 *
 * @param {String} eventName e.g. 'ViewContent'
 * @param {Object} params event parameters
 * @return {Boolean} true when the event was sent
 */
export const trackPixelEvent = (eventName, params = {}) => {
  const fbq = getFbq();
  if (!fbq || !eventName) {
    return false;
  }
  try {
    fbq('track', eventName, params);
    return true;
  } catch (e) {
    // Analytics must never break the page.
    return false;
  }
};

/**
 * Shared shape of a product event.
 *
 * @param {Array<String>} contentIds listing UUIDs
 * @param {Number|null} value total value in major units (kronor, not öre)
 * @param {String|null} currency e.g. 'SEK'
 * @param {Object} extra additional params
 */
const productEventParams = (contentIds, value, currency, extra = {}) => {
  const ids = (Array.isArray(contentIds) ? contentIds : [contentIds]).filter(Boolean);
  return {
    content_ids: ids,
    content_type: CONTENT_TYPE_PRODUCT,
    ...(value != null && !Number.isNaN(value) ? { value } : {}),
    ...(currency ? { currency } : {}),
    ...extra,
  };
};

/**
 * A shopper opened a listing page.
 *
 * @param {Object} options
 * @param {String} options.listingId listing UUID
 * @param {String} [options.title]
 * @param {Number} [options.value] price in major units
 * @param {String} [options.currency]
 */
export const trackViewContent = ({ listingId, title, value, currency } = {}) => {
  if (!listingId) {
    return false;
  }
  return trackPixelEvent(
    'ViewContent',
    productEventParams([listingId], value, currency, title ? { content_name: title } : {})
  );
};

/**
 * A shopper put a listing in the cart.
 *
 * @param {Object} options same shape as trackViewContent
 */
export const trackAddToCart = ({ listingId, title, value, currency } = {}) => {
  if (!listingId) {
    return false;
  }
  return trackPixelEvent(
    'AddToCart',
    productEventParams([listingId], value, currency, title ? { content_name: title } : {})
  );
};

/**
 * A shopper reached the payment step. Sent when the order exists but has not
 * been paid yet, which is where the embedded payment flow sends them.
 *
 * @param {Object} options
 * @param {Array<String>} options.listingIds every listing in the order
 * @param {Number} [options.value]
 * @param {String} [options.currency]
 */
export const trackInitiateCheckout = ({ listingIds, value, currency } = {}) => {
  const ids = (listingIds || []).filter(Boolean);
  if (ids.length === 0) {
    return false;
  }
  return trackPixelEvent(
    'InitiateCheckout',
    productEventParams(ids, value, currency, { num_items: ids.length })
  );
};

/**
 * Payment completed.
 *
 * @param {Object} options
 * @param {Array<String>} options.listingIds every listing in the order
 * @param {Number} [options.value] total paid, in major units
 * @param {String} [options.currency]
 */
export const trackPurchase = ({ listingIds, value, currency } = {}) => {
  const ids = (listingIds || []).filter(Boolean);
  if (ids.length === 0) {
    return false;
  }
  return trackPixelEvent(
    'Purchase',
    productEventParams(ids, value, currency, { num_items: ids.length })
  );
};

/**
 * Money in minor units (öre) to the major units Meta expects.
 *
 * @param {Number} amount
 * @param {Number} [divisor]
 * @return {Number|null}
 */
export const toMajorUnits = (amount, divisor = 100) =>
  typeof amount === 'number' && !Number.isNaN(amount) ? amount / divisor : null;
